import {useEffect} from 'react'

import { format, isToday, isYesterday, parseISO } from 'date-fns'
import { useNavigate } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from '@App/hooks'
import { configMarkets } from '@App/redux/slices/config/config.selectors'
import { addFavoriteMarket } from '@App/redux/slices/config/ConfigSlice'
import { addSuccessNotify } from '@App/redux/slices/notifications/NotificationSlice'
import {
  selectReportOrdersList,
  selectReportTradesList,
} from '@App/redux/slices/reports/selectors'
import { ROUTE_NAMES } from '@App/ui/crypto_exchange/desktop/routes/routeNames'

import {asyncGetOrders, asyncGetTrades} from '@Redux/slices/reports/ReportsSlice'
import {currentWallet} from '@Redux/slices/user/selectors'

export enum ReportsTabs {
  Transactions = 'transactions',
  Trades = 'trades',
  Orders = 'orders',
  Analytics = 'analytics',
}

export enum ReportsFilters {
  Type = 'type',
  Status = 'status',
  Period = 'period',
}

export type ReportsFilter = {
  [ReportsFilters.Period]: string
  [ReportsFilters.Status]: string[]
  [ReportsFilters.Type]: string[]
}

export type ReportTab = {
  name: string
  value: ReportsTabs
  filters: ReportsFilters[]
  showSendReport?: boolean
}

export const REPORTS_TABS: ReportTab[] = [
  {
    name: 'Transactions',
    value: ReportsTabs.Transactions,
    filters: [ReportsFilters.Type, ReportsFilters.Status, ReportsFilters.Period],
    showSendReport: true,
  },
  {
    name: 'Trades',
    value: ReportsTabs.Trades,
    filters: [ReportsFilters.Status, ReportsFilters.Period],
    showSendReport: true,
  },
  {
    name: 'Orders',
    value: ReportsTabs.Orders,
    filters: [ReportsFilters.Status, ReportsFilters.Period],
    showSendReport: true,
  },
  { name: 'Analytics', value: ReportsTabs.Analytics, filters: [ReportsFilters.Period] },
]

export const TIME_PERIOD_OPTIONS = [
  { name: '6 Hours', value: '6h' },
  { name: '12 Hours', value: '12h' },
  { name: '24 Hours', value: '24h' },
  { name: '3 Days', value: '3d' },
  { name: '1 Week', value: '1w' },
  { name: '1 Month', value: '1m' },
  { name: '2 Months', value: '2m' },
  { name: '3 Months', value: '3m' },
  { name: 'All', value: 'all' },
]

export const REPORT_TYPE_OPTIONS = [
  { name: 'Trade closed', value: 'trade_closed', separated: true },
  { name: 'Buy KO option', value: 'buy_ko_option' },
  { name: 'Sell KO option', value: 'sell_ko_option' },
  { name: 'Commissions', value: 'commissions' },
  { name: 'Overnight funding adjustment', value: 'overnight_funding_adjustment' },
  { name: 'Corrections', value: 'corrections' },
  { name: 'Bonus', value: 'bonus' },
  { name: 'Set balance', value: 'set_balance' },
]
export const REPORT_STATUS_OPTIONS = [
  { name: 'All', value: 'all', separated: true },
  { name: 'In review', value: 'in_review' },
  { name: 'Processing', value: 'processing' },
  { name: 'Processed', value: 'processed' },
  { name: 'Declined', value: 'declined' },
  { name: 'Cancelled', value: 'cancelled' },
  { name: 'Blocked', value: 'blocked' },
  { name: 'Refund', value: 'refund' },
  { name: 'Transaction void', value: 'transaction_void' },
  { name: 'Waiting for settlement', value: 'waiting_for_settlement' },
]

export const formatDate = (dateString: string): string => {
  const date = parseISO(dateString);

  if (isToday(date)) {
    return 'Today'
  }

  if (isYesterday(date)) {
    return 'Yesterday'
  }

  return format(date, 'dd MMM yyyy')
}

export const formatTime = (dateString: string): string => {
  const date = parseISO(dateString);
  return format(date, 'HH:mm')
}

export const useCopyText = () => {
  const dispatch = useAppDispatch()
  return {
    copyText: async (text: string) => {
      await navigator.clipboard.writeText(text)
      dispatch(addSuccessNotify({ message: 'Copied to clipboard' }))
    },
  }
}

export const useTrades = () => {
  const trades = useAppSelector(selectReportTradesList)
  const currWallet = useAppSelector(currentWallet)
  const dispatch = useAppDispatch()

  useEffect(() => {
    currWallet?.id && dispatch(asyncGetTrades(currWallet?.id))
  }, [currWallet?.id]);

  return { trades }
}

export const useOrders = () => {
  const orders = useAppSelector(selectReportOrdersList)
  const currWallet = useAppSelector(currentWallet)
  const dispatch = useAppDispatch()

  useEffect(() => {
    currWallet?.id && dispatch(asyncGetOrders(currWallet?.id))
  }, [currWallet?.id]);

  return { orders }
}

export const useSeeMarket = (data?: { sourceCurrency: string; targetCurrency: string }) => {
  const dispatch = useAppDispatch()
  const market = useAppSelector((state) =>
    configMarkets(state).find(
      (m) => m.source_currency === data?.sourceCurrency && m.target_currency === data?.targetCurrency,
    ),
  )
  const markets = useAppSelector(configMarkets)
  const navigate = useNavigate()

  return {
    seeMarket: (params?: { sourceCurrency: string; targetCurrency: string }) => {
      if (market) {
        dispatch(addFavoriteMarket(market))
        navigate(ROUTE_NAMES.CHARTS)
        return
      }
      if (params) {
        const _market = markets.find(
          (m) => m.source_currency === params.sourceCurrency && m.target_currency === params.targetCurrency,
        )
        if (_market) {
          dispatch(addFavoriteMarket(_market))
          navigate(ROUTE_NAMES.CHARTS)
        }
      }
    },
  }
}
